import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Loading from '../../components/Loading';
import { LEVELS, loadLibrary, type Level, type LibraryStory } from '../../lib/library';
import { languageName } from '../../lib/languages';
import { isStaleBuildError, reloadForNewBuild } from '../../lib/staleBuild';
import { wordCount } from '../../lib/reading';
import { sentences } from '../../lib/storyLesson';
import { useClassroom } from '../../state/Classroom';

// The built-in library: graded stories a teacher can assign in two clicks.
// "Assign" opens the usual assignment form with everything filled in, so the
// teacher still chooses the week, reads and pace before it goes out.

export default function Library() {
  const { active, assignments, drafts } = useClassroom();
  const [all, setAll] = useState<LibraryStory[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lang, setLang] = useState(active?.language ?? 'es');
  const [level, setLevel] = useState<Level | 'all'>('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => { loadLibrary().then(setAll).catch(e => { if (isStaleBuildError(e) && reloadForNewBuild()) return; setError(String(e?.message ?? e)); }); }, []);
  useEffect(() => { if (active?.language) setLang(active.language); }, [active?.language]);

  const langs = useMemo(() => [...new Set((all ?? []).map(s => s.lang))], [all]);
  // Already used in this class (assigned, scheduled or a draft): same text.
  const used = useMemo(() => new Set([...assignments, ...drafts].map(a => (a.story_text ?? '').trim()).filter(Boolean)), [assignments, drafts]);

  if (error) return <div className="notice bad">Couldn’t load the library: {error}</div>;
  if (!all) return <Loading what="the library" />;

  const needle = q.trim().toLowerCase();
  const list = all.filter(s => s.lang === lang && (level === 'all' || s.level === level)
    && (!needle || `${s.title} ${s.blurb} ${s.tags.join(' ')}`.toLowerCase().includes(needle)));

  return (
    <div>
      <h1>Library</h1>
      <p className="sub">
        Original graded stories, ready to assign. Each opens in the assignment form with a suggested pace and
        number of reads, so you can change anything before it goes out.
      </p>

      <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
        <select value={lang} onChange={e => setLang(e.target.value)} aria-label="Language" style={{ width: 'auto' }}>
          {langs.map(l => <option key={l} value={l}>{languageName(l)}</option>)}
          {!langs.includes(lang) && <option value={lang}>{languageName(lang)}</option>}
        </select>
        <div className="seg">
          <button type="button" className={level === 'all' ? 'on' : ''} onClick={() => setLevel('all')}>All levels</button>
          {LEVELS.filter(l => all.some(s => s.lang === lang && s.level === l.level)).map(l => (
            <button key={l.level} type="button" className={level === l.level ? 'on' : ''} onClick={() => setLevel(l.level)} title={l.label}>{l.level}</button>
          ))}
        </div>
        <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search: food, travel, preterite…"
          style={{ flex: 1, minWidth: 180 }} aria-label="Search the library" />
      </div>

      {list.length === 0 && (
        <div className="notice">
          {all.some(s => s.lang === lang)
            ? 'Nothing matches that search.'
            : `There are no ${languageName(lang)} stories in the library yet. More languages are on the way.`}
        </div>
      )}

      <div className="list">
        {list.map(s => {
          const words = wordCount(s.text);
          const isUsed = used.has(s.text.trim());
          const isOpen = open === s.id;
          return (
            <div key={s.id} className="item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
                <span className="thumb story-thumb" aria-hidden>📖</span>
                <span className="grow">
                  <span className="term">{s.title}</span>
                  <span className="gloss small">
                    <span className="pill">{s.level}</span>{' '}
                    {s.blurb} · {words} words · {sentences(s.text).length} sentences
                    {isUsed && ' · ✓ already in this class'}
                  </span>
                  <span className="small muted">{s.tags.join(' · ')}</span>
                </span>
                <button className="small" onClick={() => setOpen(isOpen ? null : s.id)}>{isOpen ? 'Hide' : 'Read'}</button>
                <Link className="button small primary" to={`/assign?kind=story&lib=${encodeURIComponent(s.id)}`}>Assign</Link>
              </div>
              {isOpen && (
                <div className="story-preview" dir="auto">
                  {s.text.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
